import * as Sentry from '@sentry/nextjs';
import { getCookie } from 'cookies-next/client';
import { pipe } from 'fp-ts/lib/function';
import { SSRConfig, appWithTranslation } from 'next-i18next/pages';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { generateDefaultSeo } from 'next-seo/pages';
import { ComponentType, ReactNode, useEffect, useState, useMemo } from 'react';

import { ErrorAsync, Http } from '@catawiki/runtime-toolkit';
import {
  I18nProvider,
  I18nInstance,
  ErrorBoundary,
  ErrorPage,
} from '@catawiki/cata-style';
import { ABSmartly, ABSmartlyWrapper, Units } from '@catawiki/ab-test';
import {
  getABSmartlySdkOptions,
  RuntimeConfigProvider,
} from '@catawiki/next-config/runtime-config';

import { LayoutAssets } from '@/shared/components/LayoutData';
import { fetchCsrf } from '@/shared/api/csrf';
import { registerAllComponents } from '@/shared/builderio';
import { Layout } from '@/shared/components/Layout';
import { COOKIES } from '@/shared/constants/cookies';
import { getUserId } from '@/shared/cookies';
import { HeaderSettings } from '@/shared/models/header';
import { captureException } from '@/shared/utils/captureException';
import { nullableData } from '@/shared/utils/http';
import { createPageI18n } from '@/shared/utils/i18n';
import { isInMobileApp } from '@/shared/utils/isInMobileApp';
import {
  getPagesRuntimeConfig,
  type PagesRuntimeConfigPageProps,
} from '@/shared/utils/runtimeConfig';
import '../src/styles/application.scss';
import '@/shared/styles/tailwind.css';
import { APP_URL } from '@/shared/constants/appRoutes';

registerAllComponents();

const noop = () => undefined;
const noopABSmartlyContext = {
  attributes: noop,
  data: () => ({}),
  experiments: () => [],
  finalize: () => Promise.resolve(),
  isFailed: () => false,
  isFinalized: () => false,
  isFinalizing: () => false,
  isReady: () => true,
  peek: () => 0,
  peekVariableValue: <T,>(_key: string, defaultValue: T) => defaultValue,
  ready: () => Promise.resolve(true),
  refresh: () => Promise.resolve(),
  track: noop,
  treatment: () => 0,
  unit: noop,
  variableKeys: () => ({}),
};

const NoopABSmartlyProvider = ABSmartly.default as unknown as ComponentType<{
  children: ReactNode;
  context: unknown;
}>;

interface SharedProps
  extends SSRConfig, HeaderSettings, Partial<PagesRuntimeConfigPageProps> {
  assetsData?: LayoutAssets;
  locale?: I18nInstance['locale'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  err?: any;
  units?: Units;
}

function MyApp({ Component, pageProps, router }: AppProps<SharedProps>) {
  const { locale, _nextI18Next } = pageProps;
  const runtimeConfig = pageProps.runtimeConfig ?? getPagesRuntimeConfig();
  const abSmartlySdkOptions = getABSmartlySdkOptions(runtimeConfig);

  const i18n = useMemo(
    () => createPageI18n(locale, _nextI18Next?.initialI18nStore || {}),
    [locale, _nextI18Next]
  );

  const [csrfToken, setCsrfToken] = useState<string>();

  const userId = pageProps.units?.userId ?? getUserId();

  useEffect(() => {
    if (userId) {
      Sentry.setUser({ id: String(userId) });
    } else {
      Sentry.setUser(null);
    }
  }, [userId]);

  useEffect(() => {
    if (!isInMobileApp()) return;
    fetchCsrf()
      .then(pipe(nullableData))
      .then((csrf) => {
        if (!csrf) return;
        setCsrfToken(csrf);
      })
      .catch(ErrorAsync.errorToLeft);
  }, []);

  useEffect(() => {
    Http.onFetchRequest((request) => {
      const token = getCookie(COOKIES.OAUTH_TOKEN);
      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }

      if (csrfToken) {
        request.headers.set(COOKIES.S_CSRF_TOKEN, csrfToken);
      }
    });
  }, [csrfToken]);

  const defaultSeo = (
    <Head>
      {generateDefaultSeo({
        openGraph: {
          type: 'website',
          locale: pageProps.locale,
          url: `https://www.catawiki.com/${pageProps.locale}/`,
          site_name: 'Catawiki',
        },
        twitter: {
          handle: '@catawiki',
          cardType: 'summary_large_image',
        },
        facebook: {
          appId: '238226522900020',
        },
        canonical: `https://www.catawiki.com/${pageProps.locale}/`,
      })}
    </Head>
  );

  const layoutContent = (
    <Layout {...pageProps}>
      {defaultSeo}
      <Component {...pageProps} />
    </Layout>
  );

  const pageLayout = abSmartlySdkOptions ? (
    <ABSmartlyWrapper
      sdkOptions={abSmartlySdkOptions}
      units={pageProps.units}
      locale={pageProps.locale || 'en'}
    >
      {layoutContent}
    </ABSmartlyWrapper>
  ) : (
    <NoopABSmartlyProvider context={noopABSmartlyContext}>
      {layoutContent}
    </NoopABSmartlyProvider>
  );

  const pageContent =
    !pageProps?.assetsData || router.route === '/_error' ? (
      <Component {...pageProps} err={pageProps.err} />
    ) : (
      <ErrorBoundary
        fallback={() => <ErrorPage withLogo appUrl={APP_URL} />}
        onError={(error) =>
          captureException(error, {
            tags: { fingerprint: 'app_error_boundary' },
          })
        }
      >
        <I18nProvider fallback={pageLayout} value={i18n}>
          {pageLayout}
        </I18nProvider>
      </ErrorBoundary>
    );

  return (
    <RuntimeConfigProvider runtimeConfig={runtimeConfig}>
      {pageContent}
    </RuntimeConfigProvider>
  );
}

export default appWithTranslation(MyApp);
